import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { Slider } from "@mui/material";
import { RefreshCcwIcon } from "lucide-react";

import { TranslatedText } from "@/components/common/text";
import { ErrorComponent } from "@/components/common/errors";
import {
    PredictionDropdown,
    FilterButton,
    PredictionMarketsMap,
    VolumeButton,
    BestOutboundCardSkeleton,
    NoSuggestionsContent,
    BestOutboundCardWithLoad,
    LockedWrapper,
    LockedTooltip,
} from "@/components/dataPages";
import { protectedRoutes } from "@/routing";
import { cn } from "@/utils/css";
import { getLocationString } from "@/utils/text";
import { markToRatio, ratioToMark, useQuery } from "@/utils/query";
import { clearBestOutboundsState, getBestOutbounds } from "@/state/bestOutboundsSlice";
import { InfoButton } from "@/components/common/buttons";
import Switch from "@mui/material/Switch";
import { chooseBestOutbound } from "@/utils/outbounds";
import { MapLegend } from "@/components/maps/MapLegend";
import { lockedMarketPotentialBestOutbounds } from "@/utils/lockedData";

export const PredictionMarketPotentialBestOutbounds = ({ isLocked = false, activeTooltip, onHover, onLeave }) => {
    const { pickup } = useSelector((state) => state.currentSearch);
    const { data: bestOutbounds, loading, error } = useSelector((state) => state.bestOutbounds);
    const [filteredOutbounds, setFilteredOutbounds] = useState([]);
    const [sortedOutbounds, setSortedOutbounds] = useState([]);
    const defaultDistanceRange = [0, 3000];
    const [distanceRange, setDistanceRange] = useState(defaultDistanceRange);
    const {
        trailerType: trailer,
        pickupZip,
        outboundRatio: urlOutboundRatio,
        pickupDate,
        withLoads,
        extendedMarket,
        pickupRadius,
    } = useQuery();
    const [withExtendedMarket, setWithExtendedMarket] = useState(extendedMarket);

    const dispatch = useDispatch();

    const [ratioMark, setRatioMark] = useState(urlOutboundRatio ? ratioToMark(urlOutboundRatio) : 2);
    const [initialLoading, setInitialLoading] = useState(true);
    // The shared bestOutbounds slice may still hold the previous page's data when
    // this page mounts; only preselect filters once our own fetch has resolved.
    const [hasOwnOutbounds, setHasOwnOutbounds] = useState(false);
    const [initialRatioChange, setInitialRatioChange] = useState(true);
    const timeoutRef = useRef(null);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const marks = [0, 1, 2, 3, 4].map((mark) => ({ value: mark }));
    const [withAvailableLoads, setWithAvailableLoads] = useState(withLoads);
    const [shouldRefreshOutbounds, setShouldRefreshOutbounds] = useState(0);
    const getOutboundsIntervalRef = useRef(null);
    const [excludedPickupMarkets, setExcludedPickupMarkets] = useState([]);

    const optimizePerItems = [
        { id: "cmi", text: "CM Index" },
        { id: "rpm", text: "RPM" },
        { id: "rate", text: "Flat rate" },
    ];

    const [optimizePer, setOptimizePer] = useState(optimizePerItems[0]);

    const handleBestOutbounds = (isAutoRefresh = false) => {
        dispatch(
            getBestOutbounds({
                trailerType: trailer,
                zip: pickupZip,
                pickupDate,
                useExtendedMarket: withExtendedMarket,
                isAutoRefresh,
                pickupRadius: pickupRadius === "market-area" ? null : pickupRadius,
            }),
        ).then(() => setHasOwnOutbounds(true));
    };

    const clearOutboundsInterval = () => {
        if (getOutboundsIntervalRef.current) {
            clearInterval(getOutboundsIntervalRef.current);
        }
    };

    useEffect(() => {
        if (isLocked) return;
        clearOutboundsInterval();
        handleBestOutbounds(false);
        getOutboundsIntervalRef.current = setInterval(() => {
            handleBestOutbounds(true);
        }, 60000);
    }, [pickupZip, pickupDate, trailer, withExtendedMarket, shouldRefreshOutbounds, pickupRadius, isLocked]);

    const [confidenceFilter, setConfidenceFilter] = useState(null);
    const emptyDuration = {
        1: false,
        2: false,
        3: false,
    };
    const [durationFilter, setDurationFilter] = useState(emptyDuration);
    const durationFilterItems = [
        { id: 1, text: "1 d" },
        { id: 2, text: "2 d" },
        { id: 3, text: "3 d" },
    ];

    const availableOutbounds = useMemo(() => {
        if (isLocked) {
            return withAvailableLoads
                ? lockedMarketPotentialBestOutbounds.filter((d) => d.numberOfLoads)
                : lockedMarketPotentialBestOutbounds;
        }
        if (bestOutbounds) {
            console.log("Best outbounds length", bestOutbounds.length);
            return withAvailableLoads ? bestOutbounds.filter((d) => d.numberOfLoads) : bestOutbounds;
        }
        return [];
    }, [bestOutbounds, withAvailableLoads, isLocked]);

    useEffect(() => {
        // Until our own fetch has resolved, the slice may hold the previous
        // page's outbounds - never preselect (or latch) based on those.
        if (!hasOwnOutbounds) {
            return;
        }
        if (initialLoading) {
            if (availableOutbounds && availableOutbounds.length) {
                console.log("Available outbounds length", availableOutbounds.length);
                let countHigh = 0;
                let countMedium = 0;
                let countLow = 0;
                availableOutbounds.forEach((outbound) => {
                    if (outbound.confidence === "high") countHigh++;
                    else if (outbound.confidence === "medium") countMedium++;
                    else if (outbound.confidence === "low") countLow++;
                });
                console.log("Best outbounds confidence (high, medium, low) ", countHigh, countMedium, countLow);
                if (countHigh + countMedium > 5) {
                    setConfidenceFilter({
                        high: true,
                        medium: true,
                        low: false,
                    });
                } else {
                    setConfidenceFilter({
                        high: false,
                        medium: false,
                        low: false,
                    });
                }
                setInitialLoading(false);
            } else {
                setConfidenceFilter({
                    high: false,
                    medium: false,
                    low: false,
                });
            }
        }
    }, [availableOutbounds, hasOwnOutbounds]);

    useEffect(() => {
        // Skip during initial loading to avoid using stale confidenceFilter
        if (initialLoading) {
            return;
        }
        if (confidenceFilter && availableOutbounds?.length) {
            const noConfidenceFilter = !confidenceFilter.high && !confidenceFilter.medium && !confidenceFilter.low;
            let sorted = chooseBestOutbound([...availableOutbounds], optimizePer, ratioMark);

            sorted = sorted.map((bo) => {
                const pickupMarket = bo.pickupName?.split(",")[0]?.trim();
                const isMarketExcluded = excludedPickupMarkets.includes(pickupMarket);
                return {
                    ...bo,
                    filtered:
                        !isMarketExcluded &&
                        bo.confidence !== "no_data" &&
                        (noConfidenceFilter ||
                            (confidenceFilter.high && bo.confidence === "high") ||
                            (confidenceFilter.medium && bo.confidence === "medium") ||
                            (confidenceFilter.low && bo.confidence === "low")) &&
                        ((!durationFilter[1] && !durationFilter[2] && !durationFilter[3]) ||
                            (durationFilter[1] && bo.tripDuration <= 1) ||
                            (durationFilter[2] && bo.tripDuration > 1 && bo.tripDuration <= 2) ||
                            (durationFilter[3] && bo.tripDuration > 2)) &&
                        bo.distance >= distanceRange[0] &&
                        bo.distance <= distanceRange[1],
                };
            });

            const filtered = sorted.filter((bo) => bo.filtered).slice(0, 6);

            setSortedOutbounds(sorted);
            console.log("Filtered outbounds data", filtered);
            setFilteredOutbounds(filtered);
        } else {
            setSortedOutbounds([]);
            setFilteredOutbounds([]);
        }
    }, [
        initialLoading,
        availableOutbounds,
        confidenceFilter,
        ratioMark,
        optimizePer,
        durationFilter,
        excludedPickupMarkets,
        distanceRange,
    ]);

    const handleRatio = (e) => {
        if (loading) return;
        setRatioMark(parseInt(e.target.value));
    };

    const handleChangedRatio = () => {
        if (loading) return;
        if (!initialRatioChange) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                const updatedParams = new URLSearchParams(searchParams);
                updatedParams.set("outboundRatio", `${markToRatio(ratioMark)}`);
                navigate(`${protectedRoutes[2].path}?${updatedParams.toString()}`);
            }, 500);
        } else {
            setInitialRatioChange(false);
        }
    };

    const mapMarkets = useMemo(() => {
        const outbounds = {};

        sortedOutbounds.forEach((outbound, index) => {
            const marketName = outbound.deliveryName.split(",")[0].trim();
            if (!outbounds[marketName]) {
                outbounds[marketName] = {
                    filtered: null,
                    unfiltered: null,
                };
            }
            const out = {
                id: outbound.deliveryZip,
                name: outbound.deliveryName,
                marketName: marketName,
                rpm: outbound.avgRPM,
                rate: outbound.avgPrice,
                CMI: outbound.CMI,
                sortIndex: outbound.confidence !== "no_data" ? index : undefined,
                confidence: outbound.confidence,
                distance: outbound.distance,
                filtered: outbound.filtered,
            };
            if (!outbounds[marketName].filtered && out.filtered) {
                outbounds[marketName].filtered = out;
            } else if (!outbounds[marketName].unfiltered && !out.filtered) {
                outbounds[marketName].unfiltered = out;
            }
        });
        return outbounds;
    }, [sortedOutbounds]);

    // Extract unique pickup market names when extended market is enabled
    const pickupMarkets = useMemo(() => {
        if (!availableOutbounds?.length) return [];
        const markets = [...new Set(availableOutbounds.map((o) => o.pickupName?.split(",")[0]?.trim()).filter(Boolean))];
        return markets;
    }, [availableOutbounds]);

    const marketLoads = useMemo(() => {
        const items = {};
        const noConfidenceFilter = !confidenceFilter?.high && !confidenceFilter?.medium && !confidenceFilter?.low;
        const noDurationFilter = !durationFilter[1] && !durationFilter[2] && !durationFilter[3];

        availableOutbounds?.forEach((o) => {
            // Apply all filters EXCEPT market exclusion
            const passesConfidence =
                o.confidence !== "no_data" &&
                (noConfidenceFilter ||
                    (confidenceFilter?.high && o.confidence === "high") ||
                    (confidenceFilter?.medium && o.confidence === "medium") ||
                    (confidenceFilter?.low && o.confidence === "low"));

            const passesDuration =
                noDurationFilter ||
                (durationFilter[1] && o.tripDuration <= 1) ||
                (durationFilter[2] && o.tripDuration > 1 && o.tripDuration <= 2) ||
                (durationFilter[3] && o.tripDuration > 2);

            const passesDistance = o.distance >= distanceRange[0] && o.distance <= distanceRange[1];

            if (passesConfidence && passesDuration && passesDistance) {
                const m = o.pickupName?.split(",")[0]?.trim();
                if (m) items[m] = (items[m] || 0) + (o.numberOfLoads ?? 0);
            }
        });
        return items;
    }, [availableOutbounds, confidenceFilter, durationFilter, distanceRange]);

    useEffect(() => {
        return () => {
            clearOutboundsInterval();
            dispatch(clearBestOutboundsState());
        };
    }, []);

    const handleDistanceSliderChange = (e) => {
        if (loading) return;
        setDurationFilter(emptyDuration);
        setDistanceRange(e.target.value);
    };

    const handleRefreshLoads = useCallback(() => {
        if (!loading) {
            setShouldRefreshOutbounds(Math.random());
        }
    }, [loading]);

    const togglePickupMarket = useCallback((marketName) => {
        setExcludedPickupMarkets((prev) => (prev.includes(marketName) ? prev.filter((m) => m !== marketName) : [...prev, marketName]));
    }, []);

    // Reset excluded markets when extended market toggle changes
    useEffect(() => {
        setExcludedPickupMarkets([]);
    }, [withExtendedMarket]);

    return (
        <div className="relative flex flex-col min-h-[576px] w-full lg:min-h-full">
            <div className="w-full flex flex-col lg:flex-row mb-6 flex-1">
                <div
                    className={cn(
                        "w-full lg:w-[calc(100% - 284px)] overflow-hidden relative min-h-[325px] lg:min-h-[360px] min-[1300px]:max-h-none min-[1300px]:min-h-[392px]",
                        {
                            "opacity-50": isLocked && activeTooltip !== "map",
                        },
                    )}
                >
                    <div className="flex flex-row gap-[10px] items-center leading-[25px] h-[38px]">
                        <div className="text-cm-p-text-primary text-base/[25px] flex items-center">
                            <div>
                                <TranslatedText>Outbound profitability from</TranslatedText>
                                {pickup ? ` ${getLocationString(pickup)}` : ""}
                            </div>
                            <div className="ml-[10px]">
                                <InfoButton
                                    element={
                                        <div className="flex flex-col text-cm-p-primary">
                                            <div className="mb-3">
                                                <TranslatedText>
                                                    Map colors show how profitable each market is, from the chosen location, based on your
                                                    selection on the right.
                                                </TranslatedText>
                                            </div>
                                            <MapLegend className="static" side="left" />
                                        </div>
                                    }
                                />
                            </div>
                        </div>
                    </div>
                    <div
                        className="flex-1 w-full h-[calc(100%-38px)]"
                        onMouseEnter={isLocked ? () => onHover && onHover("map") : undefined}
                        onMouseLeave={isLocked ? onLeave : undefined}
                    >
                        <PredictionMarketsMap
                            outbounds={mapMarkets}
                            hasError={error}
                            optimizePer={optimizePer}
                            filterOutbounds={filteredOutbounds}
                            ratioMark={ratioMark}
                            pickupMarkets={pickupMarkets}
                            excludedPickupMarkets={excludedPickupMarkets}
                            onTogglePickupMarket={togglePickupMarket}
                            isLocked={isLocked}
                            marketLoads={marketLoads}
                        />
                    </div>
                </div>
                {isLocked ? (
                    <LockedWrapper
                        isActive={activeTooltip === "prediction"}
                        onHover={() => onHover && onHover("prediction")}
                        onLeave={onLeave}
                    >
                        <div
                            className={cn(
                                "w-full lg:w-[284px] lg:min-w-[284px] flex flex-col lg:pl-[26px] min-[1300px]:py-0 justify-center",
                                {
                                    "opacity-40": loading,
                                },
                            )}
                        >
                            <div className="flex justify-between items-center h-[38px] mb-2">
                                <TranslatedText className="text-cm-p-text-primary text-sm/[22px] mr-[10px]">
                                    Include wider market area
                                </TranslatedText>
                                <Switch
                                    className="cm-toggle cm-toggle-loads"
                                    disabled={loading || isLocked}
                                    checked={!!withExtendedMarket}
                                    onChange={() => {
                                        const updatedParams = new URLSearchParams(searchParams);
                                        updatedParams.set("extendedMarket", !withExtendedMarket ? "true" : "false");
                                        setWithExtendedMarket(!withExtendedMarket);
                                        navigate(`${protectedRoutes[2].path}?${updatedParams.toString()}`);
                                    }}
                                />
                            </div>
                            <div className="flex flex-row items-center justify-between mb-[19px]">
                                <div className="flex flex-row items-center">
                                    <TranslatedText className="text-cm-p-text-primary text-sm/[22px] mr-[10px]">
                                        Optimize per
                                    </TranslatedText>
                                    <InfoButton
                                        element={
                                            <div>
                                                <TranslatedText>
                                                    Allows you to choose how we prioritize suggested outbound routes.
                                                </TranslatedText>
                                                <br />
                                                <TranslatedText>
                                                    CM Index - Considers multiple key factors to provide the most profitable suggestions.
                                                </TranslatedText>
                                            </div>
                                        }
                                    />
                                </div>
                                <PredictionDropdown
                                    items={optimizePerItems}
                                    value={optimizePer}
                                    setValue={(value) => {
                                        setOptimizePer(optimizePerItems.find((item) => item.id === value));
                                    }}
                                    disabled={loading || isLocked}
                                />
                            </div>
                            <div
                                className={cn("flex flex-col items-start mb-[19px] py-2", {
                                    "opacity-50 cursor-not-allowed": loading || optimizePer.id !== "cmi",
                                })}
                            >
                                <div className="flex flex-row items-center mb-2">
                                    <TranslatedText className="text-cm-p-text-primary text-sm/[22px] mr-[10px]">
                                        Earn more on
                                    </TranslatedText>
                                    <InfoButton
                                        element={
                                            <div>
                                                <TranslatedText>
                                                    Gives you the ability to choose whether you’d prefer to earn more on a Lane or Exit
                                                    outbound.
                                                </TranslatedText>
                                                <br />
                                                <TranslatedText>
                                                    The Lane outbound is the load you take directly from this market.
                                                </TranslatedText>
                                                <br />
                                                <TranslatedText>
                                                    The Exit outbound is the next load after you deliver the Lane outbound.
                                                </TranslatedText>
                                                <br />
                                            </div>
                                        }
                                    />
                                </div>
                                <div className="h-[34px] bg-cm-p-surface-container rounded-[48px] flex flex-row px-4 py-[6px] items-center">
                                    <TranslatedText className="mr-3 text-cm-p-text-primary text-sm/[22px] font-medium">Lane</TranslatedText>
                                    <div className="w-[138px] flex items-center">
                                        <Slider
                                            value={ratioMark}
                                            step={null}
                                            min={0}
                                            max={4}
                                            marks={marks}
                                            onChange={handleRatio}
                                            onChangeCommitted={handleChangedRatio}
                                            disabled={loading || optimizePer.id !== "cmi" || isLocked}
                                        />
                                    </div>
                                    <TranslatedText className="ml-[19px] text-cm-p-text-primary text-sm/[22px] font-medium">
                                        Exit
                                    </TranslatedText>
                                </div>
                            </div>
                            <div className="flex flex-col items-start mb-[19px] py-2">
                                <TranslatedText className="text-cm-p-text-primary text-sm/[22px] mb-2">Outbound distance</TranslatedText>
                                <div className="w-full flex flex-col items-center bg-cm-p-surface-container rounded-[16px] mb-2 pt-3 pb-2 pl-4 pr-5 range-wrap">
                                    <Slider
                                        value={distanceRange}
                                        step={50}
                                        min={0}
                                        max={isLocked ? 2500 : 3000}
                                        onChange={handleDistanceSliderChange}
                                        disabled={loading || isLocked}
                                    />
                                    <div className="text-center text-sm/[22px] text-cm-p-text-primary font-medium">
                                        {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(distanceRange[0])} mi{" "}
                                        <span className="text-cm-p-text-secondary">-</span>{" "}
                                        {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(distanceRange[1])} mi
                                    </div>
                                </div>
                                <div className="w-full grid grid-cols-3 gap-1">
                                    {durationFilterItems.map((duration) => (
                                        <FilterButton
                                            disabled={
                                                loading ||
                                                isLocked ||
                                                (duration.id === 1 && durationFilter[3] && !durationFilter[2]) ||
                                                (duration.id === 3 && durationFilter[1] && !durationFilter[2])
                                            }
                                            key={duration.id}
                                            active={durationFilter ? durationFilter[duration.id] : false}
                                            onClick={() => {
                                                setDurationFilter({ ...durationFilter, [duration.id]: !durationFilter[duration.id] });
                                                setDistanceRange(defaultDistanceRange);
                                            }}
                                        >
                                            {duration.text}
                                        </FilterButton>
                                    ))}
                                </div>
                            </div>
                            <div className="flex flex-col">
                                <div className="flex flex-row items-center mb-2">
                                    <TranslatedText className="text-cm-p-text-primary text-sm/[22px] mr-[10px]">Volume</TranslatedText>
                                    <InfoButton text="Indicates how frequently a load is driven, and how easy or difficult it is to find." />
                                </div>
                                <div className="w-full flex flex-row items-center justify-between gap-1">
                                    {["high", "medium", "low"].map((confidence, index) => (
                                        <VolumeButton
                                            disabled={!confidenceFilter || loading}
                                            key={confidence}
                                            value={confidence}
                                            level={3 - index}
                                            active={confidenceFilter ? confidenceFilter[confidence] : false}
                                            onClick={() => {
                                                if (isLocked) return;
                                                setConfidenceFilter({ ...confidenceFilter, [confidence]: !confidenceFilter[confidence] });
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </LockedWrapper>
                ) : (
                    <div
                        className={cn("w-full lg:w-[284px] lg:min-w-[284px] flex flex-col lg:pl-[26px] min-[1300px]:py-0 justify-center", {
                            "opacity-40": loading,
                        })}
                    >
                        <div className="flex justify-between items-center h-[38px] mb-2">
                            <TranslatedText className="text-cm-p-text-primary text-sm/[22px] mr-[10px]">
                                Include wider market area
                            </TranslatedText>
                            <Switch
                                className="cm-toggle cm-toggle-loads"
                                disabled={loading}
                                checked={!!withExtendedMarket}
                                onChange={() => {
                                    const updatedParams = new URLSearchParams(searchParams);
                                    updatedParams.set("extendedMarket", !withExtendedMarket ? "true" : "false");
                                    setWithExtendedMarket(!withExtendedMarket);
                                    navigate(`${protectedRoutes[2].path}?${updatedParams.toString()}`);
                                }}
                            />
                        </div>
                        <div className="flex flex-row items-center justify-between mb-[19px]">
                            <div className="flex flex-row items-center">
                                <TranslatedText className="text-cm-p-text-primary text-sm/[22px] mr-[10px]">Optimize per</TranslatedText>
                                <InfoButton
                                    element={
                                        <div>
                                            <TranslatedText>
                                                Allows you to choose how we prioritize suggested outbound routes.
                                            </TranslatedText>
                                            <br />
                                            <TranslatedText>
                                                CM Index - Considers multiple key factors to provide the most profitable suggestions.
                                            </TranslatedText>
                                        </div>
                                    }
                                />
                            </div>
                            <PredictionDropdown
                                items={optimizePerItems}
                                value={optimizePer}
                                setValue={(value) => {
                                    setOptimizePer(optimizePerItems.find((item) => item.id === value));
                                }}
                                disabled={loading}
                            />
                        </div>
                        <div
                            className={cn("flex flex-col items-start mb-[19px] py-2", {
                                "opacity-50 cursor-not-allowed": loading || optimizePer.id !== "cmi",
                            })}
                        >
                            <div className="flex flex-row items-center mb-2">
                                <TranslatedText className="text-cm-p-text-primary text-sm/[22px] mr-[10px]">Earn more on</TranslatedText>
                                <InfoButton
                                    element={
                                        <div>
                                            <TranslatedText>
                                                Gives you the ability to choose whether you'd prefer to earn more on a Lane or Exit
                                                outbound.
                                            </TranslatedText>
                                            <br />
                                            <TranslatedText>
                                                The Lane outbound is the load you take directly from this market.
                                            </TranslatedText>
                                            <br />
                                            <TranslatedText>
                                                The Exit outbound is the next load after you deliver the Lane outbound.
                                            </TranslatedText>
                                            <br />
                                        </div>
                                    }
                                />
                            </div>
                            <div className="h-[34px] bg-cm-p-surface-container rounded-[48px] flex flex-row px-4 py-[6px] items-center">
                                <TranslatedText className="mr-3 text-cm-p-text-primary text-sm/[22px] font-medium">Lane</TranslatedText>
                                <div className="w-[138px] flex items-center">
                                    <Slider
                                        value={ratioMark}
                                        step={null}
                                        min={0}
                                        max={4}
                                        marks={marks}
                                        onChange={handleRatio}
                                        onChangeCommitted={handleChangedRatio}
                                        disabled={loading || optimizePer.id !== "cmi"}
                                    />
                                </div>
                                <TranslatedText className="ml-[19px] text-cm-p-text-primary text-sm/[22px] font-medium">
                                    Exit
                                </TranslatedText>
                            </div>
                        </div>
                        <div className="flex flex-col items-start mb-[19px] py-2">
                            <TranslatedText className="text-cm-p-text-primary text-sm/[22px] mb-2">Outbound distance</TranslatedText>
                            <div className="w-full flex flex-col items-center bg-cm-p-surface-container rounded-[16px] mb-2 pt-3 pb-2 pl-4 pr-5 range-wrap">
                                <Slider
                                    value={distanceRange}
                                    step={50}
                                    min={0}
                                    max={3000}
                                    onChange={handleDistanceSliderChange}
                                    disabled={loading || isLocked}
                                />
                                <div className="text-center text-sm/[22px] text-cm-p-text-primary font-medium">
                                    {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(distanceRange[0])} mi{" "}
                                    <span className="text-cm-p-text-secondary">-</span>{" "}
                                    {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(distanceRange[1])} mi
                                </div>
                            </div>
                            <div className="w-full grid grid-cols-3 gap-1">
                                {durationFilterItems.map((duration) => (
                                    <FilterButton
                                        disabled={
                                            loading ||
                                            (duration.id === 1 && durationFilter[3] && !durationFilter[2]) ||
                                            (duration.id === 3 && durationFilter[1] && !durationFilter[2])
                                        }
                                        key={duration.id}
                                        active={durationFilter ? durationFilter[duration.id] : false}
                                        onClick={() => {
                                            setDurationFilter({ ...durationFilter, [duration.id]: !durationFilter[duration.id] });
                                            setDistanceRange(defaultDistanceRange);
                                        }}
                                    >
                                        {duration.text}
                                    </FilterButton>
                                ))}
                            </div>
                        </div>
                        <div className="flex flex-col">
                            <div className="flex flex-row items-center mb-2">
                                <TranslatedText className="text-cm-p-text-primary text-sm/[22px] mr-[10px]">Volume</TranslatedText>
                                <InfoButton text="Indicates how frequently a load is driven, and how easy or difficult it is to find." />
                            </div>
                            <div className="w-full flex flex-row items-center justify-between gap-1">
                                {["high", "medium", "low"].map((confidence, index) => (
                                    <VolumeButton
                                        disabled={!confidenceFilter || loading}
                                        key={confidence}
                                        value={confidence}
                                        level={3 - index}
                                        active={confidenceFilter ? confidenceFilter[confidence] : false}
                                        onClick={() => {
                                            setConfidenceFilter({ ...confidenceFilter, [confidence]: !confidenceFilter[confidence] });
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {isLocked ? (
                <LockedWrapper
                    isActive={activeTooltip === "outboundCards"}
                    onHover={() => onHover && onHover("outboundCards")}
                    onLeave={onLeave}
                >
                    <div className="text-cm-p-text-primary flex items-center text-base leading-[25px] mb-4">
                        <div className="mr-6">
                            <TranslatedText>Suggested outbounds</TranslatedText>
                        </div>
                        <div className="flex-1 h-[1px] bg-cm-p-outline-variation" />
                        <div className="flex items-center ml-6 gap-[10px]">
                            <TranslatedText className="text-cm-p-text-primary text-sm/[22px]">With available loads only</TranslatedText>
                            <Switch className="cm-toggle cm-toggle-loads" checked={!!withAvailableLoads} disabled />
                            <button type="buttton" disabled className="opacity-50 cursor-not-allowed">
                                <RefreshCcwIcon className="w-4 h-4 text-cm-p-accent" />
                            </button>
                        </div>
                    </div>
                    {error ? (
                        <ErrorComponent className="min-h-[452px] h-[452px] sm:min-h-[218px] sm:h-[218px] w-full min-[1300px]:min-h-[140px] min-[1300px]:h-[140px]" />
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 min-[1300px]:grid-cols-3 gap-4 h-[452px] sm:h-[302px] min-[1300px]:h-[224px]">
                            {loading ? (
                                [0, 1, 2, 3, 4, 5].map((index) => <BestOutboundCardSkeleton key={index} index={index} withLoad />)
                            ) : filteredOutbounds.length === 0 ? (
                                <NoSuggestionsContent className="col-span-1 sm:col-span-2 min-[1300px]:col-span-3" />
                            ) : (
                                filteredOutbounds.map((outbound, index) => (
                                    <BestOutboundCardWithLoad
                                        key={index}
                                        index={index}
                                        outbound={outbound}
                                        numOfLoads={outbound.numberOfLoads}
                                        withExtendedMarket={withExtendedMarket}
                                    />
                                ))
                            )}
                        </div>
                    )}
                </LockedWrapper>
            ) : (
                <>
                    <div className="text-cm-p-text-primary flex items-center text-base leading-[25px] mb-4">
                        <div className="mr-6">
                            <TranslatedText>Suggested outbounds</TranslatedText>
                        </div>
                        <div className="flex-1 h-[1px] bg-cm-p-outline-variation" />
                        <div className="flex items-center ml-6 gap-[10px]">
                            <TranslatedText className="text-cm-p-text-primary text-sm/[22px]">With available loads only</TranslatedText>
                            <Switch
                                className="cm-toggle cm-toggle-loads"
                                checked={!!withAvailableLoads}
                                onChange={() => {
                                    const updatedParams = new URLSearchParams(searchParams);
                                    updatedParams.set("withLoads", !withAvailableLoads ? "true" : "false");
                                    setWithAvailableLoads(!withAvailableLoads);
                                    navigate(`${protectedRoutes[2].path}?${updatedParams.toString()}`);
                                }}
                            />
                            <button type="buttton" onClick={handleRefreshLoads}>
                                <RefreshCcwIcon
                                    className={cn("w-4 h-4 text-cm-p-accent", {
                                        "animate-spin": loading,
                                    })}
                                />
                            </button>
                        </div>
                    </div>
                    {error ? (
                        <ErrorComponent className="min-h-[452px] h-[452px] sm:min-h-[218px] sm:h-[218px] w-full min-[1300px]:min-h-[140px] min-[1300px]:h-[140px]" />
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 min-[1300px]:grid-cols-3 gap-4 h-[452px] sm:h-[302px] min-[1300px]:h-[224px]">
                            {loading ? (
                                [0, 1, 2, 3, 4, 5].map((index) => <BestOutboundCardSkeleton key={index} index={index} withLoad />)
                            ) : filteredOutbounds.length === 0 ? (
                                <NoSuggestionsContent className="col-span-1 sm:col-span-2 min-[1300px]:col-span-3" />
                            ) : (
                                filteredOutbounds.map((outbound, index) => (
                                    <BestOutboundCardWithLoad
                                        key={index}
                                        index={index}
                                        outbound={outbound}
                                        numOfLoads={outbound.numberOfLoads}
                                        withExtendedMarket={withExtendedMarket}
                                    />
                                ))
                            )}
                        </div>
                    )}
                </>
            )}
            {isLocked && (
                <>
                    <LockedTooltip
                        content={
                            <>
                                Discover which U.S. markets <span className="font-bold">bring the strongest profits</span>, measured by CM
                                Index, rate per mile, or flat rate.
                            </>
                        }
                        tooltipId="map"
                        activeTooltip={activeTooltip}
                        width="380px"
                        positionConfig={{
                            default: { mediumScreen: { top: "84px", left: "-72px" }, desktop: { top: "84px", left: "-72px" } },
                            active: { mediumScreen: { top: "54px", left: "-105px" }, desktop: { top: "54px", left: "-105px" } },
                            leftSide: { mediumScreen: { top: "84px", left: "12px" }, desktop: { top: "84px", left: "12px" } },
                            outboundCards: { mediumScreen: { top: "38px", left: "-98px" }, desktop: { top: "38px", left: "-98px" } },
                            prediction: { mediumScreen: { top: "84px", left: "-91px" }, desktop: { top: "84px", left: "-91px" } },
                        }}
                    />
                    <LockedTooltip
                        content={
                            <>
                                {" "}
                                Let CargoMinds recommend <span className="font-bold">your next move</span> based on your preferences, such
                                as trip length and load volume.
                            </>
                        }
                        tooltipId="prediction"
                        activeTooltip={activeTooltip}
                        width="357px"
                        positionConfig={{
                            default: { mediumScreen: { top: "360px", right: "62px" }, desktop: { top: "210px", right: "248px" } },
                            active: { mediumScreen: { top: "210px", right: "301px" }, desktop: { top: "210px", right: "301px" } },
                            outboundCards: { mediumScreen: { top: "131px", right: "145px" }, desktop: { top: "131px", right: "145px" } },
                            map: { mediumScreen: { top: "410px", right: "19px" }, desktop: { top: "210px", right: "19px" } },
                            leftSide: { mediumScreen: { top: "360px", right: "42px" }, desktop: { top: "210px", right: "203px" } },
                        }}
                    />
                    <LockedTooltip
                        content={
                            <>
                                Get <span className="font-bold">suggestions for the best destinations</span> from your truck's location,
                                factoring in rate averages, RPM, trip costs, load availability, and more.
                            </>
                        }
                        tooltipId="outboundCards"
                        activeTooltip={activeTooltip}
                        width="392px"
                        positionConfig={{
                            default: { mediumScreen: { top: "481px", left: "170px" }, desktop: { top: "481px", left: "341px" } },
                            active: { mediumScreen: { top: "353px", left: "170px" }, desktop: { top: "403px", left: "349px" } },
                            prediction: { mediumScreen: { top: "481px", left: "100px" }, desktop: { top: "481px", left: "300px" } },
                            map: { mediumScreen: { top: "519px", left: "115px" }, desktop: { top: "519px", left: "365px" } },
                            leftSide: { mediumScreen: { top: "481px", left: "240px" }, desktop: { top: "481px", left: "367px" } },
                        }}
                    />
                </>
            )}
        </div>
    );
};
